import React, { useMemo, useState, useEffect, useCallback } from "react";
import { useHistory } from "react-router-dom";
import { Form, Button, Input, Col, Row, message } from "antd";
import { PlusOutlined, CloseOutlined } from "@ant-design/icons";
import { useForm, useFieldArray, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";

import { getAddReportCategorySchema } from "../../schema/report-category.schema";
import AddReportCategoryRow from "../../components/ReportCategory/AddReportCategory/AddReportCategoryRow";
import "../../components/Common/Input/styles/select.css";

import {
  CardElement,
  CenteredInputContainer,
  HMGSelect,
  ProjectSelect,
  HotelSelect,
  RoundedCheckbox,
} from "../../components/Common";
import { useGetProjectReportCategories } from "../../hooks/project.hooks";
import { useAddReportCategory } from "../../hooks/report-category.hooks";

const AddReportCategory = () => {
  const history = useHistory();
  const [hmg, setHmg] = useState();
  const [project, setProject] = useState();
  const [hotels, setHotels] = useState([]);

  const [projectList, setProjectList] = useState();

  const { data: projectReportCategories } = useGetProjectReportCategories(
    project,
    { enabled: !!project },
  );

  const { mutateAsync: addReportCategory, isLoading } = useAddReportCategory();

  const reportCategorySchema = useMemo(
    () =>
      getAddReportCategorySchema(
        projectReportCategories
          ? projectReportCategories.map((el) => el.name)
          : [],
      ),
    [projectReportCategories],
  );

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(reportCategorySchema),
  });
  const { fields, remove, append } = useFieldArray({
    control,
    name: "categories",
  });

  const check = useMemo(() => !!project, [project]);

  const setHmgRef = useCallback((list) => {
    if (list?.length === 1) setHmg(list[0]._id);
  }, []);

  const setProjectRef = useCallback((list) => {
    setProjectList(list);
  }, []);

  const setHotelListCallback = useCallback((list) => {
    setHotels(list.map((hotel) => hotel._id));
  }, []);

  useEffect(() => {
    if (!project) setHotels([]);
  }, [project]);

  useEffect(() => {
    setProject(
      projectList?.length === 1 && hmg ? projectList[0]._id : undefined,
    );
  }, [projectList, hmg]);

  useEffect(() => {
    reset();
  }, [hotels]);

  const submitFn = async (data) => {
    const apiResultSet = await Promise.allSettled(
      data.categories.map(async (category) => {
        const apiResult = await addReportCategory({
          project_id: project,
          apply_all_hotels: check,
          name: category.name,
        });
        return apiResult;
      }),
    );
    if (
      apiResultSet.filter((result) => result.status === "rejected").length > 0
    ) {
      apiResultSet.forEach((response, index) => {
        if (response.status === "rejected")
          message.error(
            response?.reason?.response?.data?.message ?? "Server Error Occured",
          );
      });
    } else {
      message.success("Report Categories successfully created");
      setHmg(undefined);
    }
  };

  return (
    <CardElement
      title="Report Category"
      subHeading="Add"
      showClose
      onClose={() => history.push("/report-category/view")}
    >
      <CenteredInputContainer>
        <Form wrapperCol={{ span: 24 }} labelCol={{ span: 24 }}>
          <Form.Item label="Hotel Management Group">
            <HMGSelect
              value={hmg}
              onChange={setHmg}
              placeholder="Hotel Management Group"
              setHmgListRef={setHmgRef}
            />
          </Form.Item>
          <Form.Item label="Project">
            <ProjectSelect
              value={project}
              onChange={setProject}
              hmgId={hmg}
              placeholder="Project"
              disabled={!hmg}
              setProjectListRef={setProjectRef}
            />
          </Form.Item>
          {project && (
            <>
              <div style={{ marginBottom: 12 }}>
                <RoundedCheckbox checked={check}>
                  Apply same report category for all the hotel in the Group
                </RoundedCheckbox>
              </div>
              <Form.Item label="Hotel (S)">
                <HotelSelect
                  value={hotels}
                  multiple
                  projectId={project}
                  setHotelListRef={setHotelListCallback}
                  placeholder="Hotel (S)"
                  className="select-set-max-height select-hide-remove-icon"
                />
              </Form.Item>
              {fields.map((field, index) => (
                <AddReportCategoryRow
                  key={field.id}
                  field={field}
                  index={index}
                  errors={errors}
                  control={control}
                  remove={remove}
                />
              ))}
              <Form.Item>
                <Button block onClick={() => append()}>
                  <PlusOutlined /> Add Report Category
                </Button>
              </Form.Item>
            </>
          )}
          <Form.Item>
            <Row justify="space-between" gutter={16}>
              <Col span={12}>
                <Button
                  block
                  disabled={isLoading}
                  onClick={() => setHmg(undefined)}
                >
                  Cancel
                </Button>
              </Col>
              <Col span={12}>
                <Button
                  block
                  type="primary"
                  onClick={handleSubmit(submitFn)}
                  disabled={!fields?.length > 0}
                  loading={isLoading}
                >
                  Save
                </Button>
              </Col>
            </Row>
          </Form.Item>
        </Form>
      </CenteredInputContainer>
    </CardElement>
  );
};

AddReportCategory.propTypes = {};

export default AddReportCategory;

